import React, { FC, useEffect, useMemo, useState } from 'react';
import { useLazyQuery } from '@apollo/client';
import DeleteIcon from '@mui/icons-material/Delete';
import { Box, IconButton, Typography } from '@mui/material';
import {
  Controller,
  FieldPathByValue,
  get,
  useFormContext,
} from 'react-hook-form';
import { OperatorEnum } from 'src/components/DashboardTable/types';
import { defaultOperators } from 'src/components/Forms/CustomFormulaForm/Filters/FilterCollapse';
import {
  InnerContainer,
  Input,
  TitleContainer,
} from 'src/components/Forms/CustomFormulaForm/Filters/FilterCollapse/styled';
import { CustomFormulaValues } from 'src/components/Forms/CustomFormulaForm/index';
import { Autocomplete } from 'src/components/Forms/FormInputs/Autocomplete';
import { MultipleAutocomplete } from 'src/components/Forms/FormInputs/MultipleAutocomplete';
import { MultiValueInput } from 'src/components/Forms/FormInputs/MultiValueInput';
import { RegionSearchInput } from 'src/components/Forms/FormInputs/RegionSearchInput';
import { Select } from 'src/components/Forms/FormInputs/Select';
import { getIconForMetric } from 'src/utils';
import { GET_FILTER_AVAILABLE_VALUES } from 'src/utils/gql/queries';
import {
  Filter,
  FilterValueType,
  GetFilterAvailableValuesVariables,
  MetricFilterAvailableValue,
  MetricFilterItem,
  Query,
} from 'src/utils/gql/types';

interface RuleProps {
  ruleName: string;
  filters: Filter[];
  filterPath: FieldPathByValue<CustomFormulaValues, MetricFilterItem>;
  loading: boolean;
  onDelete: () => void;
  keySelection?: boolean;
  onChangeDimension: (key: string) => void;
}

export const Rule: FC<RuleProps> = ({
  filters,
  filterPath,
  ruleName,
  loading,
  onDelete,
  keySelection,
  onChangeDimension,
}) => {
  const {
    watch,
    setValue,
    control,
    formState: { errors },
  } = useFormContext<CustomFormulaValues>();

  const [fetchAvailableValues, { loading: availableValuesLoading }] =
    useLazyQuery<
      Pick<Query, 'getFilterAvailableValues'>,
      GetFilterAvailableValuesVariables
    >(GET_FILTER_AVAILABLE_VALUES);

  // const onChangeDimension = (key: string) => {
  //   const operator = filters.find((f) => f.key === key)?.operators[0].key || '';
  //
  //   setValue(`${filterPath}.key`, key, { shouldValidate: true });
  //   setValue(`${filterPath}.operator`, operator);
  //   setValue(`${filterPath}.values`, []);
  // };

  const onChangeOperator = () => setValue(`${filterPath}.values`, []);

  const key = watch(`${filterPath}.key`);
  const operator = watch(`${filterPath}.operator`);
  const valuesInputError = get(errors, `${filterPath}.values`);

  const selectedDimensionFilter = useMemo(
    () => filters.find((f) => f.key === key),
    [filters, key],
  );

  const [availableValues, setAvailableValues] = useState<
    MetricFilterAvailableValue[]
  >(selectedDimensionFilter?.availableValues ?? []);

  useEffect(() => {
    if (selectedDimensionFilter?.shouldAvailableValuesBeRequested) {
      fetchAvailableValues({
        variables: {
          filterKey: selectedDimensionFilter.key,
          connectorType: selectedDimensionFilter.type,
        },
        onCompleted: ({ getFilterAvailableValues: newAvailableValues }) => {
          // Dedupe immutably by key to avoid React duplicate key warnings
          const base = selectedDimensionFilter?.availableValues ?? [];
          const fetched = (newAvailableValues ?? []).map((value) => ({
            key: value,
            label: value,
          }));
          const map = new Map<string, { key: string; label: string }>();
          for (const opt of base) {
            if (opt?.key)
              map.set(opt.key, { key: opt.key, label: opt.label ?? opt.key });
          }
          for (const opt of fetched) {
            if (opt?.key && !map.has(opt.key)) map.set(opt.key, opt);
          }
          setAvailableValues(Array.from(map.values()));
        },
      });
    } else {
      // Ensure base values are deduped even without fetch
      const base = selectedDimensionFilter?.availableValues ?? [];
      const map = new Map<string, { key: string; label: string }>();
      for (const opt of base) {
        if (opt?.key)
          map.set(opt.key, { key: opt.key, label: opt.label ?? opt.key });
      }
      setAvailableValues(Array.from(map.values()));
    }
  }, [
    fetchAvailableValues,
    selectedDimensionFilter?.key,
    selectedDimensionFilter?.shouldAvailableValuesBeRequested,
    selectedDimensionFilter?.type,
    selectedDimensionFilter?.availableValues,
  ]);

  const inputType =
    selectedDimensionFilter?.expectedReturnValueType ===
    FilterValueType.ArrayInt
      ? 'number'
      : 'text';

  const hasAvailableValues = !!availableValues.length;
  const inOrNotIn =
    operator === OperatorEnum.In || operator === OperatorEnum.NotIn;

  // Special case per user_region: usa SearchInput invece di dropdown
  const isUserRegionFilter = selectedDimensionFilter?.key === 'user_region';

  const showMultiAutocomplete =
    (!selectedDimensionFilter || hasAvailableValues) &&
    inOrNotIn &&
    !isUserRegionFilter;
  const showMultiValueInput =
    selectedDimensionFilter &&
    !hasAvailableValues &&
    inOrNotIn &&
    !isUserRegionFilter;
  const showSearchInput = isUserRegionFilter && inOrNotIn;
  const showInput = !inOrNotIn && !isUserRegionFilter;

  const selectOptions =
    selectedDimensionFilter?.operators &&
    selectedDimensionFilter.operators.length > 0
      ? selectedDimensionFilter.operators
      : [];

  return (
    <Box>
      <TitleContainer>
        <Typography>{ruleName}</Typography>
        <IconButton onClick={onDelete}>
          <DeleteIcon />
        </IconButton>
      </TitleContainer>
      <InnerContainer>
        {keySelection ? (
          <Autocomplete<Filter, string>
            name={`${filterPath}.key`}
            loading={loading}
            placeholder="Select dimension"
            options={filters}
            optionIcon={getIconForMetric(filters[0]?.type)}
            onManualChange={onChangeDimension}
            isOptionEqualToValue={(option, value) => option.key === value}
            getOptionLabel={(option: Filter | string) =>
              typeof option === 'string'
                ? filters.find((opt) => opt.key === option)?.label || ''
                : option.label
            }
          />
        ) : null}
        <Select
          name={`${filterPath}.operator`}
          options={selectOptions}
          onManualChange={onChangeOperator}
          disabled={selectOptions.length === 0}
        />

        {showMultiAutocomplete && (
          <MultipleAutocomplete
            name={`${filterPath}.values`}
            options={availableValues}
            loading={loading || availableValuesLoading}
          />
        )}

        {showSearchInput && (
          <RegionSearchInput
            name={`${filterPath}.values`}
            placeholder=""
            helperText=""
          />
        )}

        {showInput && (
          <Controller
            control={control}
            name={`${filterPath}.values`}
            render={({ field: { onChange, onBlur, value } }) => (
              <Input
                autoComplete="off"
                placeholder="Write a value"
                error={!!valuesInputError?.message}
                value={value}
                onChange={(e) => {
                  const value = e.target.value ? [e.target.value] : [];
                  onChange(value);
                }}
                onBlur={onBlur}
                type={inputType}
              />
            )}
          />
        )}
        {showMultiValueInput && (
          <MultiValueInput name={`${filterPath}.values`} type={inputType} />
        )}
      </InnerContainer>
    </Box>
  );
};
